import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";

import { Button } from "src/base-components/Button";
import { CodeEditor } from "src/base-components/CodeInput/CodeEditor";
import {
  normalizeJsonSchema,
  normalizeNullableUnions,
  normalizeNumericConstraints,
} from "src/jsonSchema/normalizeJsonSchema";
import {
  isEmptySchemaString,
  noAllOf,
  ValidationConfig,
  validateJsonSchema,
} from "src/jsonSchema/validateJsonSchema";
import { defaultSchema } from "src/schema/schemaMappingUtils";

export type RawJsonSchemaEditorProps = {
  initialSchema?: string;
  onSave: (schema: string) => void;
  onDiscard?: () => void;
  onChange?: (schema: string, isDirty: boolean, canSave: boolean) => void;
  title?: string;
  subtitle?: string;
  topDecoration?: React.ReactNode;
};

export type RawJsonSchemaEditorRef = {
  save: () => void;
};

const defaultValidationConfig: ValidationConfig = {
  customValidators: [noAllOf],
  normalizers: [normalizeNumericConstraints, normalizeNullableUnions],
};

const RawJsonSchemaEditorComponent = forwardRef<
  RawJsonSchemaEditorRef,
  RawJsonSchemaEditorProps
>(({ initialSchema, onSave, onDiscard, onChange, topDecoration }, ref) => {
  const [rawJsonSchema, setRawJsonSchema] = useState<string>(
    initialSchema || `{}`,
  );
  const [isDirty, setIsDirty] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Update the JSON schema when initialSchema changes, but only if not dirty
  useEffect(() => {
    if (initialSchema && !isDirty) {
      setRawJsonSchema(initialSchema);
      const error = validateJsonSchema(initialSchema, defaultValidationConfig);
      setValidationError(error?.message ?? null);
      // Notify about the initial validation state
      onChange?.(initialSchema, false, !error);
    }
  }, [initialSchema, isDirty, onChange]);

  const handleChange = (newSchema: string) => {
    setRawJsonSchema(newSchema);

    // Check if actually different from initial schema
    const isActuallyDirty = newSchema !== (initialSchema || "{}");
    setIsDirty(isActuallyDirty);

    // Validate on change but don't prevent editing
    const error = validateJsonSchema(newSchema, defaultValidationConfig);
    setValidationError(error?.message ?? null);

    onChange?.(newSchema, isActuallyDirty, !error);
  };

  const handleSave = useCallback(() => {
    if (isEmptySchemaString(rawJsonSchema)) {
      const defaultString = JSON.stringify(defaultSchema, null, 2);
      onSave(defaultString);
      onChange?.(defaultString, false, true);
      setIsDirty(false);
      return;
    }

    const error = validateJsonSchema(rawJsonSchema, defaultValidationConfig);
    setValidationError(error?.message ?? null);
    if (error) {
      return; // prevent save if invalid
    }

    // Parse, normalize, and re-stringify the schema
    try {
      const parsed = JSON.parse(rawJsonSchema);
      const normalized = normalizeJsonSchema(
        parsed,
        defaultValidationConfig.normalizers ?? [],
      );
      const normalizedString = JSON.stringify(normalized, null, 2);
      onSave(normalizedString);
      onChange?.(normalizedString, false, true);
    } catch {
      // If parsing fails, just save as-is (validation should have caught this)
      onSave(rawJsonSchema);
      onChange?.(rawJsonSchema, false, true);
    } finally {
      setIsDirty(false);
    }
  }, [rawJsonSchema, onSave, onChange]);

  // Expose methods via ref
  useImperativeHandle(
    ref,
    () => ({
      save: handleSave,
    }),
    [handleSave],
  );

  return (
    <div
      className="flex h-full flex-col gap-2 p-2 pt-3"
      data-loc="raw-json-schema-editor"
    >
      {topDecoration}

      {validationError && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3">
          <div className="flex items-start gap-2">
            <div className="flex-shrink-0">
              <div className="flex h-5 w-5 items-center justify-center rounded-full bg-red-100">
                <span className="font-medium text-red-600 text-sm">!</span>
              </div>
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-inter-normal-12px text-red-700 text-sm">
                {validationError}
              </p>
            </div>
          </div>
        </div>
      )}

      <div
        className="min-h-0 flex-1 rounded-lg"
        data-loc="json-schema-code-editor-wrapper"
      >
        <CodeEditor
          backgroundColor="white"
          basicSetup={{
            lineNumbers: true,
            highlightActiveLine: true,
          }}
          dataLoc="json-schema-editor-editor"
          drawErrorBorder={false}
          fontFamily="inter"
          language="jsonschema"
          value={rawJsonSchema}
          autoFocus
          highlightOnFocus
          lineWrapping
          onChange={handleChange}
        />
      </div>

      <div className="flex justify-end gap-3">
        {onDiscard && (
          <Button
            dataLoc="json-schema-editor-cancel"
            variant="secondary"
            onClick={onDiscard}
          >
            Cancel
          </Button>
        )}
        <div className="w-[125px]">
          <Button
            dataLoc="json-schema-editor-save"
            disabled={!!validationError || !isDirty}
            variant="primary"
            fullWidth
            onClick={handleSave}
          >
            Save
          </Button>
        </div>
      </div>
    </div>
  );
});

RawJsonSchemaEditorComponent.displayName = "RawJsonSchemaEditor";

export const RawJsonSchemaEditor = RawJsonSchemaEditorComponent;
